import { Component, inject, signal, ViewContainerRef } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import {
  CapacitorBarcodeScanner,
  CapacitorBarcodeScannerScanOrientation,
  CapacitorBarcodeScannerTypeHint,
} from '@capacitor/barcode-scanner';
import { Capacitor } from '@capacitor/core';
import { LEERKR8_API_SERVICE } from '@core/saves/leerkr8/leerkr8-api.service.interface';
import { CodeReaderService } from '@features/code-readers/code-reader.service';
import { lastValueFrom } from 'rxjs';
import { PortalButtonDirective } from '../../../portal-button/portal-button.directive';
import { PortalInputComponent } from '../../../portal-input/portal-input.component';
import { PortalPanelComponent } from '../../../portal-panel/portal-panel.component';
import { ACTIVATION_SERVICE } from '../activation.service.interface';

@Component({
  imports: [ReactiveFormsModule, PortalPanelComponent, PortalInputComponent, PortalButtonDirective],
  templateUrl: './activate-device.component.html',
  styleUrl: './activate-device.component.css',
})
export class ActivateDeviceComponent {
  showSuccessMessage = signal(false);

  protected form = new FormGroup({
    code: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.minLength(9), Validators.maxLength(9)],
    }),
    deviceName: new FormControl('placeholder', {
      nonNullable: false,
    }),
  });

  private readonly api = inject(LEERKR8_API_SERVICE);
  private readonly activationService = inject(ACTIVATION_SERVICE);
  private readonly router = inject(Router);
  private readonly codeReaderService = inject(CodeReaderService);
  private readonly vcr = inject(ViewContainerRef);

  async onSubmit() {
    if (!this.form.valid) {
      throw new Error('Invalid form');
    }

    const deviceId = crypto.randomUUID();
    const data = await lastValueFrom(this.api.activateDevice(this.form.value.code!, deviceId, this.form.value.deviceName!));

    this.activationService.setActivationData({
      deviceName: this.form.value.deviceName!,
      deviceIdentifier: deviceId,
      instituteName: data.instituteName,
      token: data.token,
    });

    // Show success message with animation
    this.showSuccessMessage.set(true);

    // Wait 2 seconds before navigating
    setTimeout(async () => {
      await this.router.navigate(['/select-player-group']);
    }, 2000);
  }

  async scanBarcode() {
    const isMobile = Capacitor.isNativePlatform();
    if (isMobile) {
      const code = await CapacitorBarcodeScanner.scanBarcode({
        hint: CapacitorBarcodeScannerTypeHint.CODE_39,
        scanOrientation: CapacitorBarcodeScannerScanOrientation.LANDSCAPE,
      });

      this.form.patchValue({
        code: code.ScanResult,
      });
      await this.onSubmit();
    } else {
      // get user media to ensure we have access to the camera
      try {
        await this.codeReaderService.requestCameraAccess();
      } catch (error) {
        window.alert('Camera access denied or unavailable. Check your permissions and try again.');
        return;
      }
      const code = await this.codeReaderService.readBarcode(this.vcr);
      console.log(code);
      this.form.patchValue({ code });
      await this.onSubmit();
    }
  }

  navigateToSelectDeviceType() {
    this.router.navigate(['/selectDeviceType']);
  }
}
